import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Target, 
  CheckCircle, 
  Clock, 
  Calendar,
  FileText,
  ArrowLeft,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import { toDateInputValue, findWeekContainingDate } from '../utils/weekDates';

const getCurrentWeekId = (weekRows) => {
  if (!Array.isArray(weekRows) || weekRows.length === 0) return null;
  const now = new Date();
  const nowUtcMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

  const current = weekRows.find((week) => {
    const start = new Date(week.start_date);
    const end = new Date(week.end_date);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
    const startUtc = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
    const endUtc = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
    return nowUtcMs >= startUtc && nowUtcMs <= endUtc;
  });
  if (current) return current.id;

  const sorted = [...weekRows].sort(
    (a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
  );
  return sorted[0]?.id ?? weekRows[0].id;
};

const formatDuration = (seconds) => {
  const total = Math.max(0, Number(seconds) || 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const truncateText = (value, max = 40) => {
  const text = String(value || '');
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 3))}...`;
};

const isLikelyUrl = (value) => {
  try {
    const parsed = new URL(String(value || '').trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (error) {
    return false;
  }
};

const normalizeUrlForHref = (value, options = {}) => {
  const { schemelessRequiresMultiLabelHost = false } = options;
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (isLikelyUrl(raw)) return raw;
  const withProtocol = `https://${raw.replace(/^\/+/, '')}`;
  try {
    const parsed = new URL(withProtocol);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    if (schemelessRequiresMultiLabelHost) {
      const host = parsed.hostname.toLowerCase();
      if (host !== 'localhost' && !host.includes('.')) return '';
    }
    return parsed.href;
  } catch (error) {
    return '';
  }
};

const Dashboard = () => {
  const { user } = useAuth();
  const isCompliance = user?.role === 'compliance';
  const [weeks, setWeeks] = useState([]);
  const [targets, setTargets] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [expandedTargetId, setExpandedTargetId] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completingTarget, setCompletingTarget] = useState(null);
  const [completeContentUrl, setCompleteContentUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [updatingTarget, setUpdatingTarget] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const isValidHttpUrl = (value) => {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch (error) {
      return false;
    }
  };

  const headerWeekDateInputValue = useMemo(
    () => toDateInputValue(selectedWeek?.start_date),
    [selectedWeek]
  );

  const fetchWeeks = useCallback(async () => {
    try {
      const response = await axios.get('/api/weeks');
      setWeeks(response.data);
      if (response.data.length > 0) {
        const currentWeekId = getCurrentWeekId(response.data);
        const currentWeek = response.data.find((w) => w.id === currentWeekId) || response.data[0];
        setSelectedWeek(currentWeek);
      }
    } catch (error) {
      toast.error('Failed to load weeks');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTargets = useCallback(async () => {
    if (!selectedWeek) return;
    
    try {
      const query = isCompliance
        ? `/api/targets?week_id=${selectedWeek.id}`
        : `/api/targets?week_id=${selectedWeek.id}&user_id=${user?.id}`;
      const response = await axios.get(query);
      setTargets(response.data);
    } catch (error) {
      toast.error('Failed to load targets');
    }
  }, [selectedWeek, user?.id, isCompliance]);

  useEffect(() => {
    fetchWeeks();
  }, [fetchWeeks]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const updateTarget = async (targetId, status, evidence = '', evidenceFile = null) => {
    setUpdatingTarget(targetId);
    
    try {
      await axios.put(`/api/targets/${targetId}`, {
        status,
        content_url: evidence,
        evidence_file: evidenceFile
      });
      
      toast.success('Target updated successfully');
      fetchTargets(); // Refresh the list
    } catch (error) {
      toast.error('Failed to update target');
    } finally {
      setUpdatingTarget(null);
    }
  };

  const updateContentFlags = async (target, updates) => {
    setUpdatingTarget(target.id);
    try {
      const payloadUpdates = isCompliance
        ? { is_qc: updates.is_qc ?? target.is_qc }
        : {
            is_qc: updates.is_qc ?? target.is_qc,
            added_to_client_sheet: updates.added_to_client_sheet ?? target.added_to_client_sheet
          };
      await axios.put(`/api/targets/${target.id}`, {
        status: target.status,
        evidence: target.evidence || '',
        ...payloadUpdates
      });
      toast.success('Content status updated');
      fetchTargets();
    } catch (error) {
      toast.error('Failed to update content status');
    } finally {
      setUpdatingTarget(null);
    }
  };

  const openCompleteModal = (target) => {
    setCompletingTarget(target);
    setCompleteContentUrl(String(target?.evidence || '').trim());
    setShowCompleteModal(true);
  };

  const closeCompleteModal = () => {
    setShowCompleteModal(false);
    setCompletingTarget(null);
    setCompleteContentUrl('');
  };

  const submitCompleteWithContentUrl = async (e) => {
    e.preventDefault();
    if (!completingTarget) return;
    const contentUrl = String(completeContentUrl || '').trim();
    if (!contentUrl) {
      toast.error('Content link is required to complete');
      return;
    }
    if (!isValidHttpUrl(contentUrl)) {
      toast.error('Content link must be a valid URL');
      return;
    }
    await updateTarget(completingTarget.id, 'completed', contentUrl);
    closeCompleteModal();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success-100 text-success-800';
      case 'in_progress':
        return 'bg-warning-100 text-warning-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const groupedByClient = useMemo(() => {
    const map = new Map();
    targets.forEach((target) => {
      const key = target.client_id ? String(target.client_id) : 'none';
      if (!map.has(key)) {
        map.set(key, {
          clientId: key,
          clientName: target.client_name || 'No Client',
          rows: []
        });
      }
      map.get(key).rows.push(target);
    });

    return Array.from(map.values()).map((entry) => {
      const total = entry.rows.length;
      const completed = entry.rows.filter((t) => t.status === 'completed').length;
      const inProgress = entry.rows.filter((t) => t.status === 'in_progress').length;
      return { ...entry, total, completed, inProgress };
    }).sort((a, b) => a.clientName.localeCompare(b.clientName));
  }, [targets]);

  const selectedClientGroup = useMemo(
    () => groupedByClient.find((entry) => entry.clientId === String(selectedClientId)),
    [groupedByClient, selectedClientId]
  );

  useEffect(() => {
    if (selectedClientId && !groupedByClient.some((entry) => entry.clientId === String(selectedClientId))) {
      setSelectedClientId('');
    }
  }, [groupedByClient, selectedClientId]);

  useEffect(() => {
    setExpandedTargetId(null);
  }, [selectedClientId, selectedWeek?.id]);

  const getTargetElapsedSeconds = (target) => {
    if (target.status === 'completed') return Number(target.duration_seconds || 0);
    if (target.status === 'in_progress' && target.started_at) {
      const startMs = new Date(target.started_at).getTime();
      if (Number.isNaN(startMs)) return 0;
      return Math.max(0, Math.round((nowTick - startMs) / 1000));
    }
    return 0;
  };

  const getContentStatusRowClass = (target) => {
    if (target.added_to_client_sheet) return 'bg-green-50';
    if (target.is_qc) return 'bg-yellow-50';
    return '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Content</h1>
          <p className="text-gray-600">Track and update content targets</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Calendar className="h-5 w-5 text-gray-400 shrink-0" />
          <label className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
            <span className="whitespace-nowrap">Jump to week</span>
            <input
              type="date"
              value={headerWeekDateInputValue}
              onChange={(e) => {
                const picked = e.target.value;
                const week = findWeekContainingDate(weeks, picked);
                if (!week) {
                  toast.error('No week covers that date — pick a day inside an existing week range');
                  return;
                }
                setSelectedWeek(week);
              }}
              className="input w-auto max-w-[11rem]"
            />
          </label>
          {selectedWeek && (
            <span className="text-sm text-gray-600 whitespace-nowrap">
              Week {selectedWeek.week_number}, {selectedWeek.year}
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Target className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Total Targets</dt>
                <dd className="text-lg font-medium text-gray-900">{targets.length}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-8 w-8 text-success-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                <dd className="text-lg font-medium text-gray-900">
                  {targets.filter(t => t.status === 'completed').length}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-8 w-8 text-warning-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">In Progress</dt>
                <dd className="text-lg font-medium text-gray-900">
                  {targets.filter(t => t.status === 'in_progress').length}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {!selectedClientId ? (
        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Clients for Week {selectedWeek?.week_number}</h2>
          {groupedByClient.length === 0 ? (
            <div className="text-center py-8">
              <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No targets assigned for this week</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {groupedByClient.map((entry) => (
                <button
                  key={entry.clientId}
                  type="button"
                  onClick={() => setSelectedClientId(entry.clientId)}
                  className="text-left rounded-lg p-6 transition cursor-pointer bg-gray-50 border hover:-translate-y-0.5 hover:shadow"
                  style={{ borderColor: '#e1e5e9' }}
                >
                  <h3 className="font-semibold text-gray-900 mb-3">{entry.clientName}</h3>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500 uppercase text-xs">Targets</p>
                      <p className="font-semibold text-primary-600">{entry.total}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 uppercase text-xs">Completed</p>
                      <p className="font-semibold text-success-600">{entry.completed}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 uppercase text-xs">In Progress</p>
                      <p className="font-semibold text-warning-600">{entry.inProgress}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold text-gray-900">Content</h2>
              <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm font-semibold">
                Week {selectedWeek?.week_number}, {selectedWeek?.year}
              </span>
              <div className="flex items-center gap-4 text-sm text-gray-700">
                <span className="font-semibold">Status:</span>
                <label className="inline-flex items-center gap-2">
                  <span className="inline-block h-3 w-3 rounded-sm bg-yellow-50 border border-yellow-200" />
                  QC&apos;d
                </label>
                <label className="inline-flex items-center gap-2">
                  <span className="inline-block h-3 w-3 rounded-sm bg-green-50 border border-green-200" />
                  Added To Client Sheet
                </label>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-secondary inline-flex items-center gap-2 h-10 px-4 whitespace-nowrap"
              onClick={() => setSelectedClientId('')}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Article Title/Page URL</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Content</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client URL</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">QC&apos;d</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added To Client Sheet</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(selectedClientGroup?.rows || []).map((target) => {
                  const isExpanded = String(expandedTargetId) === String(target.id);
                  const titleHref = normalizeUrlForHref(target.title, {
                    schemelessRequiresMultiLabelHost: true
                  });
                  const titleIsUrl = Boolean(titleHref);
                  const contentHref = normalizeUrlForHref(target.evidence);
                  const clientUrlHref = normalizeUrlForHref(target.client_url);
                  return (
                    <React.Fragment key={target.id}>
                      <tr className={`${getContentStatusRowClass(target)} cursor-pointer`} onClick={() => setExpandedTargetId(isExpanded ? null : target.id)}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />}
                            {titleIsUrl ? (
                              <a
                                href={titleHref}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-secondary py-1 px-3"
                                onClick={(e) => e.stopPropagation()}
                              >
                                View
                              </a>
                            ) : (
                              <span title={target.title || ''}>{truncateText(target.title || '—', 40)}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {target.user_full_name || target.username || target.user_email || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {contentHref ? (
                            <a
                              href={contentHref}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-secondary py-1 px-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              View
                            </a>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {clientUrlHref ? (
                            <a
                              href={clientUrlHref}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-secondary py-1 px-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              View
                            </a>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{target.is_qc ? 'Yes' : 'No'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{target.added_to_client_sheet ? 'Yes' : 'No'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(target.status)}`}>
                            {target.status.replace('_', ' ')}
                          </span>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className={getContentStatusRowClass(target)}>
                          <td colSpan={7} className="px-4 pb-4 pt-0">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div className="border border-gray-200 rounded-lg p-3">
                                <p className="text-xs uppercase text-gray-500 mb-2">Task</p>
                                <p className="text-sm text-gray-700">Status: {target.status.replace('_', ' ')}</p>
                                {(target.status === 'in_progress' || target.status === 'completed') && (
                                  <p className="text-sm text-gray-700">Timer: {formatDuration(getTargetElapsedSeconds(target))}</p>
                                )}
                              </div>
                              <div className="border border-gray-200 rounded-lg p-3">
                                <p className="text-xs uppercase text-gray-500 mb-2">Content Statuses</p>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    className={`btn text-xs ${target.is_qc ? 'btn-primary' : 'btn-secondary'}`}
                                    disabled={updatingTarget === target.id}
                                    onClick={() => updateContentFlags(target, { is_qc: !target.is_qc })}
                                  >
                                    QC&apos;d: {target.is_qc ? 'Yes' : 'No'}
                                  </button>
                                  {!isCompliance && (
                                    <button
                                      type="button"
                                      className={`btn text-xs ${target.added_to_client_sheet ? 'btn-success' : 'btn-secondary'}`}
                                      disabled={updatingTarget === target.id}
                                      onClick={() => updateContentFlags(target, { added_to_client_sheet: !target.added_to_client_sheet })}
                                    >
                                      Added: {target.added_to_client_sheet ? 'Yes' : 'No'}
                                    </button>
                                  )}
                                </div>
                              </div>
                              {!isCompliance && (
                                <div className="border border-gray-200 rounded-lg p-3">
                                  <p className="text-xs uppercase text-gray-500 mb-2">Task Actions</p>
                                  <div className="flex flex-wrap gap-2">
                                    {target.status !== 'in_progress' && target.status !== 'completed' && (
                                      <button
                                        onClick={() => updateTarget(target.id, 'in_progress')}
                                        disabled={updatingTarget === target.id}
                                        className="btn btn-primary text-sm"
                                      >
                                        Start
                                      </button>
                                    )}
                                    {target.status !== 'completed' && (
                                      <button
                                        onClick={() => openCompleteModal(target)}
                                        disabled={updatingTarget === target.id}
                                        className="btn btn-success text-sm"
                                      >
                                        Complete
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                              {target.evidence && (
                                <div className="md:col-span-3 border border-gray-200 rounded-lg p-3">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <FileText className="h-4 w-4 text-gray-500" />
                                    <span className="text-sm font-medium text-gray-700">Content Link</span>
                                  </div>
                                  <p className="text-sm text-gray-600">{target.evidence}</p>
                                  {contentHref && (
                                    <a
                                      href={contentHref}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="btn btn-secondary py-1 px-3 mt-2 inline-flex"
                                    >
                                      View
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCompleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Complete Target</h3>
            <p className="text-sm text-gray-600 mb-4">
              Add the content link to mark this target as completed.
            </p>
            <form onSubmit={submitCompleteWithContentUrl} className="space-y-4">
              <div>
                <label className="label">Content Link *</label>
                <input
                  type="url"
                  className="input"
                  value={completeContentUrl}
                  onChange={(e) => setCompleteContentUrl(e.target.value)}
                  placeholder="https://example.com/article"
                  required
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="btn btn-success flex-1"
                  disabled={updatingTarget === completingTarget?.id}
                >
                  {updatingTarget === completingTarget?.id ? 'Completing...' : 'Complete'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary flex-1"
                  onClick={closeCompleteModal}
                  disabled={updatingTarget === completingTarget?.id}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 